import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { VideoTile } from './VideoTile'
import { MeetingControls } from './MeetingControls'
import { AudioSettingsDialog, DEFAULT_AUDIO_SETTINGS } from './AudioSettingsDialog'
import type { AudioProcessingSettings } from './AudioSettingsDialog'
import { ParticipantInfo, useMediaDevices } from '@/hooks'
import type { AudioQualitySettings } from '@/hooks/useLiveKitRoom'
import { Users, Copy, Check, Pin, PinOff } from 'lucide-react'

// Types for pinnable items (can be a participant's camera or their screen share)
type PinnableItem = {
  id: string // unique identifier
  type: 'camera' | 'screenshare'
  participant: ParticipantInfo
}

interface MeetingRoomProps {
  roomId: string
  participants: ParticipantInfo[]
  localParticipant: ParticipantInfo | null
  isMuted: boolean
  isCameraOff: boolean
  isScreenSharing: boolean
  isBackgroundBlurEnabled: boolean
  isBackgroundBlurLoading: boolean
  blurStrength?: number
  showParticipants: boolean
  onToggleMute: () => void
  onToggleCamera: () => void
  onToggleScreenShare: () => void
  onToggleBackgroundBlur: () => void
  onBlurStrengthChange?: (strength: number) => void
  onToggleParticipants: () => void
  onSwitchAudioInput: (deviceId: string) => Promise<void>
  onSwitchVideoInput: (deviceId: string) => Promise<void>
  onSwitchAudioOutput: (deviceId: string) => Promise<void>
  onApplyAudioSettings: (settings: AudioQualitySettings) => Promise<void>
  onLeave: () => void
}

// Local storage keys
const PARTICIPANT_VOLUMES_KEY = 'meeting-participant-volumes'
const AUDIO_SETTINGS_KEY = 'meeting-audio-settings'

// Load saved audio settings from localStorage
const loadSavedAudioSettings = (): AudioProcessingSettings => {
  try {
    const saved = localStorage.getItem(AUDIO_SETTINGS_KEY)
    if (saved) {
      return { ...DEFAULT_AUDIO_SETTINGS, ...JSON.parse(saved) }
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_AUDIO_SETTINGS
}

// Load saved volumes from localStorage
const loadSavedVolumes = (): Record<string, number> => {
  try {
    const saved = localStorage.getItem(PARTICIPANT_VOLUMES_KEY)
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

export function MeetingRoom({
  roomId,
  participants,
  localParticipant,
  isMuted,
  isCameraOff,
  isScreenSharing,
  isBackgroundBlurEnabled,
  isBackgroundBlurLoading,
  blurStrength = 50,
  showParticipants,
  onToggleMute,
  onToggleCamera,
  onToggleScreenShare,
  onToggleBackgroundBlur,
  onBlurStrengthChange,
  onToggleParticipants,
  onSwitchAudioInput,
  onSwitchVideoInput,
  onSwitchAudioOutput,
  onApplyAudioSettings,
  onLeave,
}: MeetingRoomProps) {
  const [copied, setCopied] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [audioSettings, setAudioSettings] = useState<AudioProcessingSettings>(loadSavedAudioSettings)
  const [participantVolumes, setParticipantVolumes] = useState<Record<string, number>>(loadSavedVolumes)

  // Save audio settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(audioSettings))
  }, [audioSettings])
  const [pinnedId, setPinnedId] = useState<string | null>(null) // ID of pinned item (camera or screen share)

  // Handle volume change for a participant
  const handleVolumeChange = useCallback((identity: string, volume: number) => {
    setParticipantVolumes(prev => {
      const updated = { ...prev, [identity]: volume }
      // Save to localStorage
      localStorage.setItem(PARTICIPANT_VOLUMES_KEY, JSON.stringify(updated))
      return updated
    })

    // Apply volume to the audio track
    const participant = participants.find(p => p.identity === identity)
    if (participant?.audioTrack) {
      // LiveKit audio tracks have setVolume method
      try {
        (participant.audioTrack as any).setVolume(volume)
      } catch (err) {
        console.error('Failed to set volume:', err)
      }
    }
  }, [participants])

  // Apply saved volumes when participants change
  useEffect(() => {
    participants.forEach(participant => {
      if (!participant.isLocal && participant.audioTrack) {
        const savedVolume = participantVolumes[participant.identity]
        if (savedVolume !== undefined) {
          try {
            (participant.audioTrack as any).setVolume(savedVolume)
          } catch (err) {
            // Volume setting not supported
          }
        }
      }
    })
  }, [participants, participantVolumes])

  // Get media devices for settings dialog
  const {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    selectedAudioInputId,
    selectedAudioOutputId,
    selectedVideoInputId,
    selectAudioInput,
    selectAudioOutput,
    selectVideoInput,
  } = useMediaDevices()

  // Build list of all pinnable items (cameras and screen shares)
  const pinnableItems = useMemo((): PinnableItem[] => {
    const items: PinnableItem[] = []

    participants.forEach(participant => {
      // Add camera as pinnable item
      items.push({
        id: `camera-${participant.identity}`,
        type: 'camera',
        participant,
      })

      // Add screen share as separate pinnable item if sharing
      if (participant.isScreenSharing && participant.screenTrack) {
        items.push({
          id: `screen-${participant.identity}`,
          type: 'screenshare',
          participant,
        })
      }
    })

    return items
  }, [participants])

  // Get all screen shares
  const screenShares = useMemo(() => {
    return pinnableItems.filter(item => item.type === 'screenshare')
  }, [pinnableItems])

  // Determine which item is currently pinned (auto-pin first screen share if none pinned)
  const effectivePinnedId = useMemo(() => {
    // If something is explicitly pinned and still exists, use it
    if (pinnedId && pinnableItems.some(item => item.id === pinnedId)) {
      return pinnedId
    }
    // Auto-pin first screen share if any exist
    if (screenShares.length > 0) {
      return screenShares[0].id
    }
    return null
  }, [pinnedId, pinnableItems, screenShares])

  // Get the pinned item
  const pinnedItem = useMemo(() => {
    return pinnableItems.find(item => item.id === effectivePinnedId) || null
  }, [pinnableItems, effectivePinnedId])

  // Handle pin/unpin
  const handlePin = useCallback((itemId: string) => {
    setPinnedId(prev => prev === itemId ? null : itemId)
  }, [])

  // Get grid class based on item count - optimized for smaller screens
  const getGridClass = (itemCount: number) => {
    if (itemCount === 1) return 'grid-cols-1 max-w-2xl mx-auto'
    if (itemCount === 2) return 'grid-cols-1 sm:grid-cols-2 max-w-5xl mx-auto'
    if (itemCount <= 4) return 'grid-cols-1 sm:grid-cols-2 max-w-6xl mx-auto'
    if (itemCount <= 6) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  }

  // Get items for the sidebar (all items except pinned)
  const sidebarItems = useMemo(() => {
    if (!pinnedItem) return []
    return pinnableItems.filter(item => item.id !== effectivePinnedId)
  }, [pinnableItems, pinnedItem, effectivePinnedId])

  const copyRoomLink = async () => {
    const link = `${window.location.origin}/meeting/${roomId}`
    await navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-[var(--text-main)]">Meeting Room</h2>
          <span className="px-2 py-1 bg-[var(--bg-element)] rounded text-sm text-[var(--text-muted)]">
            {roomId}
          </span>
        </div>
        <button
          onClick={copyRoomLink}
          className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] rounded-lg text-sm text-[var(--text-muted)] transition-colors"
        >
          {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video grid */}
        <div className="flex-1 p-4 overflow-y-auto">
          {pinnedItem ? (
            // Pinned layout: large pinned item + sidebar of others
            <div className="flex flex-col lg:flex-row gap-4 h-full">
              {/* Pinned item (large) - takes most of the space */}
              <div className="flex-1 relative min-h-[300px] lg:min-h-0">
                <VideoTile
                  name={pinnedItem.type === 'screenshare'
                    ? `${pinnedItem.participant.name}'s screen`
                    : pinnedItem.participant.name}
                  videoTrack={pinnedItem.type === 'screenshare'
                    ? pinnedItem.participant.screenTrack
                    : pinnedItem.participant.videoTrack}
                  audioTrack={pinnedItem.type === 'screenshare'
                    ? pinnedItem.participant.screenAudioTrack
                    : pinnedItem.participant.audioTrack}
                  isMuted={pinnedItem.type === 'screenshare'
                    ? !pinnedItem.participant.screenAudioTrack
                    : pinnedItem.participant.isMuted}
                  isCameraOff={pinnedItem.type === 'screenshare' ? false : pinnedItem.participant.isCameraOff}
                  isScreenShare={pinnedItem.type === 'screenshare'}
                  isActiveSpeaker={pinnedItem.participant.isSpeaking}
                  isLocal={pinnedItem.participant.isLocal}
                  isPinned={true}
                  onPin={() => handlePin(pinnedItem.id)}
                  volume={participantVolumes[pinnedItem.id] ?? 1}
                  onVolumeChange={!pinnedItem.participant.isLocal || pinnedItem.type === 'screenshare'
                    ? (vol) => handleVolumeChange(pinnedItem.id, vol)
                    : undefined}
                />
              </div>
              {/* Sidebar with all other items */}
              <div className="lg:w-64 flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto lg:overflow-x-hidden">
                {sidebarItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative w-32 lg:w-full flex-shrink-0 aspect-video cursor-pointer"
                    onClick={() => handlePin(item.id)}
                  >
                    <VideoTile
                      name={item.type === 'screenshare'
                        ? `${item.participant.name}'s screen`
                        : item.participant.name}
                      videoTrack={item.type === 'screenshare'
                        ? item.participant.screenTrack
                        : item.participant.videoTrack}
                      audioTrack={item.type === 'screenshare'
                        ? item.participant.screenAudioTrack
                        : item.participant.audioTrack}
                      isMuted={item.type === 'screenshare'
                        ? !item.participant.screenAudioTrack
                        : item.participant.isMuted}
                      isCameraOff={item.type === 'screenshare' ? false : item.participant.isCameraOff}
                      isScreenShare={item.type === 'screenshare'}
                      isActiveSpeaker={item.participant.isSpeaking}
                      isLocal={item.participant.isLocal}
                      audioLevel={item.participant.audioLevel}
                      volume={participantVolumes[item.id] ?? 1}
                      onVolumeChange={!item.participant.isLocal || item.type === 'screenshare'
                        ? (vol) => handleVolumeChange(item.id, vol)
                        : undefined}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Regular grid layout - use auto-rows to size based on content
            <div className={`grid ${getGridClass(pinnableItems.length)} gap-4 auto-rows-fr place-items-stretch`}>
              {pinnableItems.map((item) => (
                <div
                  key={item.id}
                  className="relative w-full min-h-[200px] cursor-pointer"
                  style={{ aspectRatio: '16/9' }}
                  onClick={() => handlePin(item.id)}
                >
                  <VideoTile
                    name={item.type === 'screenshare'
                      ? `${item.participant.name}'s screen`
                      : item.participant.name}
                    videoTrack={item.type === 'screenshare'
                      ? item.participant.screenTrack
                      : item.participant.videoTrack}
                    audioTrack={item.type === 'screenshare'
                      ? item.participant.screenAudioTrack
                      : item.participant.audioTrack}
                    isMuted={item.type === 'screenshare'
                      ? !item.participant.screenAudioTrack
                      : item.participant.isMuted}
                    isCameraOff={item.type === 'screenshare' ? false : item.participant.isCameraOff}
                    isScreenShare={item.type === 'screenshare'}
                    isActiveSpeaker={item.participant.isSpeaking}
                    isLocal={item.participant.isLocal}
                    audioLevel={item.participant.audioLevel}
                    volume={participantVolumes[item.id] ?? 1}
                    onVolumeChange={!item.participant.isLocal || item.type === 'screenshare'
                      ? (vol) => handleVolumeChange(item.id, vol)
                      : undefined}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Participants panel */}
        {showParticipants && (
          <div className="w-72 border-l border-[var(--border)] bg-[var(--bg-card)] p-4 overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} className="text-[var(--text-muted)]" />
              <h3 className="font-medium text-[var(--text-main)]">
                Participants ({participants.length})
              </h3>
            </div>
            <div className="space-y-2">
              {participants.map((participant) => (
                <div
                  key={participant.identity}
                  className="flex items-center gap-3 p-2 rounded-lg bg-[var(--bg-element)]"
                >
                  <div className="w-8 h-8 rounded-full bg-[var(--primary)] flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {participant.name[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--text-main)] truncate">
                      {participant.name}
                      {participant.isLocal && ' (You)'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      {participant.isMuted && <span>Muted</span>}
                      {participant.isCameraOff && <span>Camera off</span>}
                      {participant.isScreenSharing && <span>Sharing</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <MeetingControls
        isMuted={isMuted}
        isCameraOff={isCameraOff}
        isScreenSharing={isScreenSharing}
        isBackgroundBlurEnabled={isBackgroundBlurEnabled}
        isBackgroundBlurLoading={isBackgroundBlurLoading}
        showParticipants={showParticipants}
        participantCount={participants.length}
        onToggleMute={onToggleMute}
        onToggleCamera={onToggleCamera}
        onToggleScreenShare={onToggleScreenShare}
        onToggleBackgroundBlur={onToggleBackgroundBlur}
        onToggleParticipants={onToggleParticipants}
        onOpenSettings={() => setShowSettings(true)}
        onLeave={onLeave}
      />

      {/* Settings Dialog */}
      <AudioSettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        audioInputDevices={audioInputDevices}
        audioOutputDevices={audioOutputDevices}
        videoInputDevices={videoInputDevices}
        selectedAudioInputId={selectedAudioInputId}
        selectedAudioOutputId={selectedAudioOutputId}
        selectedVideoInputId={selectedVideoInputId}
        onAudioInputChange={(deviceId) => {
          selectAudioInput(deviceId) // Update local state
          onSwitchAudioInput(deviceId) // Switch LiveKit device
        }}
        onAudioOutputChange={(deviceId) => {
          selectAudioOutput(deviceId) // Update local state
          onSwitchAudioOutput(deviceId) // Switch LiveKit device
        }}
        onVideoInputChange={(deviceId) => {
          selectVideoInput(deviceId) // Update local state
          onSwitchVideoInput(deviceId) // Switch LiveKit device
        }}
        audioSettings={audioSettings}
        onAudioSettingsChange={(newSettings) => {
          setAudioSettings(newSettings)
          // Apply ALL audio settings to LiveKit when they change
          onApplyAudioSettings({
            highQualityMode: newSettings.highQualityMode,
            noiseSuppression: newSettings.noiseSuppression,
            echoCancellation: newSettings.echoCancellation,
            autoGainControl: newSettings.autoGainControl,
            // Custom audio processing settings
            noiseGateEnabled: newSettings.noiseGateEnabled,
            noiseGateThreshold: newSettings.noiseGateThreshold,
            noiseGateAttack: newSettings.noiseGateAttack ?? 10,
            noiseGateRelease: newSettings.noiseGateRelease ?? 150,
            gainEnabled: newSettings.gainEnabled,
            gainValue: newSettings.gainValue,
          })
        }}
        // Video processing props
        isBackgroundBlurEnabled={isBackgroundBlurEnabled}
        isBackgroundBlurLoading={isBackgroundBlurLoading}
        blurStrength={blurStrength}
        onToggleBackgroundBlur={onToggleBackgroundBlur}
        onBlurStrengthChange={onBlurStrengthChange}
        isCameraOff={isCameraOff}
      />
    </div>
  )
}
